/**
 * The formula block's symbol keyboard.
 *
 * A compact strip of groups over a grid of keys, sitting directly under the
 * formula field. It is deliberately *not* a modal palette: writing a formula is
 * a loop of "type, look, reach for a symbol", and anything that covers the
 * formula, or takes the creator away from the field to be dismissed, breaks that
 * loop. So the keyboard expands in place, keeps focus in the field, and inserts
 * at the caret.
 *
 * The symbols themselves, and the caret maths, live in `#/lib/formulaKeyboard`.
 */
import {useCallback, useMemo, useRef, useState} from 'react'
import {
  type NativeSyntheticEvent,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  type TextInputSelectionChangeEventData,
  TouchableOpacity,
  View,
} from 'react-native'
import {Feather} from '@expo/vector-icons'
import {Trans, useLingui} from '@lingui/react/macro'

import {FormulaNotation} from '#/lib/flashbookTex/notation'
import {parseFormula} from '#/lib/flashbookTex/parse'
import {
  type Caret,
  deleteBeforeCaret,
  findFormulaKeys,
  FORMULA_GROUPS,
  FORMULA_SLOT,
  insertFormulaSnippet,
} from '#/lib/formulaKeyboard'
import {StudioTextField} from '#/components/studio/StudioTextField'
import {type StudioChrome} from '#/themes-studio/chrome'
import {useStudioChrome, useStudioStyles} from '#/themes-studio/useChrome'

type Props = {
  label: string
  value: string
  placeholder?: string
  onChange: (v: string) => void
  accent: string
}

/**
 * The formula field, with the keyboard attached.
 *
 * The field tracks its own caret so a key lands where the creator is typing
 * rather than at the end of the line — which is the difference between a tool
 * and a symbol dump.
 */
export function FlashBookFormulaField({
  label,
  value,
  placeholder,
  onChange,
  accent,
}: Props) {
  const fx = useStyles()
  const chrome = useStudioChrome()
  const {t: l} = useLingui()
  const inputRef = useRef<TextInput>(null)
  const caretRef = useRef<Caret>({start: value.length, end: value.length})
  const [open, setOpen] = useState(false)
  const [groupId, setGroupId] = useState(FORMULA_GROUPS[0].id)
  const [query, setQuery] = useState('')
  // Only set around an insert: passing a stale selection every render would
  // fight the creator's own typing on web.
  const [pending, setPending] = useState<Caret | null>(null)

  const group = useMemo(
    () => FORMULA_GROUPS.find(g => g.id === groupId) ?? FORMULA_GROUPS[0],
    [groupId],
  )
  const results = useMemo(() => findFormulaKeys(query), [query])
  const keys = results.length ? results : group.keys

  /*
   * The formula as it will be *printed*.
   *
   * The creator writes source, and source is a poor mirror: `\frac{x+1}{2x-3}`
   * and `(x+1)/(2x-3)` look nothing alike, and the whole point of the block is
   * that the book draws the first. Before this the only way to find out what a
   * formula would look like was to leave the block and preview the book — which
   * is exactly when a typo is most expensive to discover. So the field draws the
   * notation underneath itself, from the same parser and the same renderer the
   * book uses, so what the creator sees here is what the reader gets, not an
   * approximation of it.
   *
   * `parseFormula` reports what it could not typeset rather than throwing, and
   * the panel repeats it in the creator's own spelling: an unknown command is
   * shown as written in the drawing (so the mistake is visible) *and* named in a
   * note (so it is findable).
   */
  const preview = useMemo(() => parseFormula(value), [value])
  const showsPreview = value.trim().length > 0

  const write = useCallback(
    (next: string, caret: Caret) => {
      caretRef.current = caret
      setPending(caret)
      onChange(next)
      inputRef.current?.focus()
    },
    [onChange],
  )

  const onKeyPress = useCallback(
    (snippet: string) => {
      const {text, selection} = insertFormulaSnippet(
        value,
        caretRef.current,
        snippet,
      )
      write(text, selection)
    },
    [value, write],
  )

  const onSelectionChange = useCallback(
    (e: NativeSyntheticEvent<TextInputSelectionChangeEventData>) => {
      const {start, end} = e.nativeEvent.selection
      caretRef.current = {start, end}
      if (pending && pending.start === start && pending.end === end) {
        // The caret we asked for has landed; stop constraining the selection.
        setPending(null)
      }
    },
    [pending],
  )

  return (
    <View style={fx.field}>
      <View style={fx.labelRow}>
        <Text style={[fx.label, {color: accent}]}>{label.toUpperCase()}</Text>
        <TouchableOpacity
          accessibilityRole="button"
          accessibilityLabel={open ? l`Hide symbols` : l`Show symbols`}
          accessibilityHint={l`Opens the maths, physics and shape keyboard`}
          onPress={() => {
            setOpen(v => !v)
            inputRef.current?.focus()
          }}
          style={[fx.toggle, open && {borderColor: accent + 'aa'}]}>
          <Text
            style={[fx.toggleTxt, {color: open ? accent : chrome.textMuted}]}>
            <Trans>Σ Symbols</Trans>
          </Text>
          <Feather
            name={open ? 'chevron-up' : 'chevron-down'}
            size={12}
            color={open ? accent : chrome.textMuted}
          />
        </TouchableOpacity>
      </View>

      {/*
        The formula's own field: one line while it is empty, growing with the
        notation. It used to open at a flat 64px and grow in 24px steps, and it
        is the studio's field now for the same reason the prose fields are.
      */}
      <StudioTextField
        ref={inputRef}
        accessibilityLabel={label}
        accessibilityHint={placeholder}
        style={fx.input}
        value={value}
        selection={pending ?? undefined}
        onChangeText={onChange}
        onSelectionChange={onSelectionChange}
        placeholder={placeholder}
        placeholderTextColor={chrome.textFaint}
        textAlignVertical="top"
        maxHeight={200}
      />

      {showsPreview && (
        <View
          testID="flashbookFormulaPreview"
          style={[fx.preview, {borderColor: chrome.border}]}>
          <FormulaNotation
            testID="flashbookFormulaPreviewNotation"
            node={preview.node}
            size={22}
            color={chrome.text}
            align="center"
          />
          {preview.problems.length > 0 && (
            <Text testID="flashbookFormulaProblems" style={fx.warn}>
              {l`Not typeset: ${preview.problems.join('  ')}`}
            </Text>
          )}
        </View>
      )}

      {open && (
        <View style={fx.pad}>
          <View style={fx.padTop}>
            <TextInput
              accessibilityLabel={l`Search symbols`}
              accessibilityHint={l`Finds a symbol by name, e.g. lambda`}
              style={fx.search}
              value={query}
              onChangeText={setQuery}
              placeholder={l`Search symbols… (lambda, integral, angle)`}
              placeholderTextColor={chrome.textFaint}
              autoCorrect={false}
            />
            <TouchableOpacity
              accessibilityRole="button"
              accessibilityLabel={l`Delete last character`}
              accessibilityHint={l`Removes the character before the caret`}
              onPress={() => {
                const {text, selection} = deleteBeforeCaret(
                  value,
                  caretRef.current,
                )
                write(text, selection)
              }}
              style={fx.tool}>
              <Feather name="delete" size={15} color={chrome.text} />
            </TouchableOpacity>
            <TouchableOpacity
              accessibilityRole="button"
              accessibilityLabel={l`Clear formula`}
              accessibilityHint={l`Empties the formula field`}
              onPress={() => write('', {start: 0, end: 0})}
              style={fx.tool}>
              <Feather name="x" size={15} color={chrome.text} />
            </TouchableOpacity>
          </View>

          {!results.length && (
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={fx.tabs}>
              {FORMULA_GROUPS.map(g => {
                const active = g.id === group.id
                return (
                  <TouchableOpacity
                    accessibilityRole="button"
                    accessibilityLabel={l`${g.label} symbols`}
                    accessibilityHint={l`Shows this group of symbols`}
                    key={g.id}
                    onPress={() => setGroupId(g.id)}
                    style={[
                      fx.tab,
                      active && {
                        borderColor: accent + '88',
                        backgroundColor: accent + '22',
                      },
                    ]}>
                    <Text style={[fx.tabTxt, active && {color: accent}]}>
                      {g.label}
                    </Text>
                  </TouchableOpacity>
                )
              })}
            </ScrollView>
          )}

          <View style={fx.grid}>
            {keys.map(key => {
              /*
               * What the key will actually *write*, which for a structure key is
               * the useful thing to say: a creator tapping `a⁄b` cannot know from
               * the glyph that it writes `\frac{}{}`, and the LaTeX is what they
               * will see in the field a moment later.
               */
              const writes = key.insert?.split(FORMULA_SLOT).join('')
              const hint = writes
                ? l`Writes ${writes} at the cursor`
                : l`Adds ${key.glyph} at the cursor`
              return (
                <TouchableOpacity
                  accessibilityRole="button"
                  accessibilityLabel={l`Insert ${key.name}`}
                  accessibilityHint={hint}
                  key={`${key.name}-${key.glyph}`}
                  onPress={() => onKeyPress(key.insert ?? key.glyph)}
                  style={fx.key}>
                  <Text style={fx.keyTxt}>{key.glyph}</Text>
                </TouchableOpacity>
              )
            })}
          </View>
        </View>
      )}
    </View>
  )
}

const makeStyles = (chrome: StudioChrome) =>
  StyleSheet.create({
    field: {gap: 6},
    labelRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    label: {fontSize: 11, fontWeight: '800', letterSpacing: 0.6},
    toggle: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      borderWidth: 1,
      borderColor: chrome.borderStrong,
      borderRadius: 14,
      paddingHorizontal: 9,
      paddingVertical: 4,
    },
    toggleTxt: {fontSize: 11, fontWeight: '700'},
    input: {
      borderWidth: 1,
      borderColor: chrome.borderStrong,
      backgroundColor: chrome.fill,
      borderRadius: 10,
      paddingHorizontal: 12,
      paddingVertical: 10,
      color: chrome.text,
      fontSize: 17,
      letterSpacing: 0.6,
    },
    /*
     * The preview reads as *paper*, not as a second field: a quiet hairline and
     * the surface behind the block cards, so the notation is plainly the thing
     * that will be printed rather than a control the creator types into.
     */
    preview: {
      borderWidth: 1,
      backgroundColor: chrome.surface,
      borderRadius: 10,
      paddingHorizontal: 12,
      paddingVertical: 10,
      gap: 4,
    },
    warn: {
      color: chrome.textMuted,
      fontSize: 11.5,
      lineHeight: 16,
      textAlign: 'center',
    },
    pad: {
      borderWidth: 1,
      borderColor: chrome.borderStrong,
      backgroundColor: chrome.fill,
      borderRadius: 12,
      padding: 8,
      gap: 8,
    },
    padTop: {flexDirection: 'row', alignItems: 'center', gap: 6},
    search: {
      flex: 1,
      borderWidth: 1,
      borderColor: chrome.borderStrong,
      borderRadius: 8,
      paddingHorizontal: 9,
      paddingVertical: 6,
      color: chrome.text,
      fontSize: 12.5,
    },
    tool: {
      width: 32,
      height: 32,
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 8,
      borderWidth: 1,
      borderColor: chrome.borderStrong,
    },
    tabs: {gap: 6, paddingVertical: 1},
    tab: {
      borderWidth: 1,
      borderColor: chrome.borderStrong,
      borderRadius: 14,
      paddingHorizontal: 10,
      paddingVertical: 4,
    },
    tabTxt: {fontSize: 11.5, fontWeight: '700', color: chrome.textMuted},
    grid: {flexDirection: 'row', flexWrap: 'wrap', gap: 6},
    key: {
      minWidth: 36,
      height: 36,
      paddingHorizontal: 6,
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 8,
      borderWidth: 1,
      borderColor: chrome.borderStrong,
      backgroundColor: chrome.fill,
    },
    keyTxt: {color: chrome.text, fontSize: 16, fontWeight: '600'},
  })

/** Styles for the appearance the app is in. */
const useStyles = () => useStudioStyles(makeStyles)
